import React from 'react';

import styled, { css } from 'styled-components';

import media from 'apps/auth/theme/media';

type RowType = 'listItem' | 'span';
type RowJustify = 'start' | 'center' | 'end' | 'between';
type RowIndent =
    | '0'
    | '4px'
    | '8px'
    | '12px'
    | '16px'
    | '24px'
    | '32px'
    | '48px'
    | '64px'
    | 'adaptive-8-16'
    | 'adaptive-16-24'
    | 'adaptive-24-32'
    | 'adaptive-32-48'
    | 'adaptive-48-64';

type NativeRowProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'type'>;

interface StyledRowProps extends NativeRowProps {
    flex?: boolean;
    direction?: 'row' | 'column';
    align?: 'start' | 'center' | 'end';
    justify?: RowJustify;
    indent?: RowIndent;
    overflow?: boolean;
    textAlign?: 'center' | 'right';
    flexwrap?: boolean;
    marginTopDesktop?: string;
    marginBottomDesktop?: string;
    marginTop?: string;
    marginBottom?: string;
    $isSpan?: boolean;
}

const shouldForwardRowProp = (prop: string) =>
    ![
        'flex',
        'direction',
        'align',
        'justify',
        'indent',
        'overflow',
        'textAlign',
        'flexwrap',
        'marginTopDesktop',
        'marginBottomDesktop',
        'marginTop',
        'marginBottom',
        '$isSpan',
    ].includes(prop);

const getAlignment = (props: StyledRowProps) => {
    switch (props.align) {
        case 'start':
            return 'start';
        case 'center':
            return 'center';
        case 'end':
            return 'end';
        default:
            return undefined;
    }
};

const getJustify = (props: StyledRowProps) => {
    switch (props.justify) {
        case 'start':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'end':
            return 'flex-end';
        case 'between':
            return 'space-between';
        default:
            return undefined;
    }
};

const getBottomIndent = (props: StyledRowProps) => {
    switch (props.indent) {
        case '4px':
            return '4px';
        case '8px':
            return '8px';
        case '12px':
            return '12px';
        case '16px':
            return '16px';
        case '24px':
            return '24px';
        case '32px':
            return '32px';
        case '48px':
            return '48px';
        case '64px':
            return '64px';
        case 'adaptive-8-16':
            return '8px';
        case 'adaptive-16-24':
            return '16px';
        case 'adaptive-24-32':
            return '24px';
        case 'adaptive-32-48':
            return '32px';
        case 'adaptive-48-64':
            return '48px';
        default:
            return null;
    }
};

const getDesktopBottomIndent = (props: StyledRowProps) => {
    switch (props.indent) {
        case '4px':
            return '4px';
        case '8px':
            return '8px';
        case '12px':
            return '12px';
        case '16px':
            return '16px';
        case '24px':
            return '24px';
        case '32px':
            return '32px';
        case '48px':
            return '48px';
        case '64px':
            return '64px';
        case 'adaptive-8-16':
            return '16px';
        case 'adaptive-16-24':
            return '24px';
        case 'adaptive-24-32':
            return '32px';
        case 'adaptive-32-48':
            return '48px';
        case 'adaptive-48-64':
            return '64px';
        default:
            return null;
    }
};

const getDirection = (props: StyledRowProps) => {
    if (props.flex) {
        return props.direction === 'column' ? 'column' : 'row';
    }
    return null;
};

const getTextAlign = (props: StyledRowProps) => {
    switch (props.textAlign) {
        case 'center':
            return 'center';
        case 'right':
            return 'right';
        default:
            return null;
    }
};

const CommonCss = css<StyledRowProps>`
    display: ${(props) => (props.flex && props.$isSpan ? 'inline-flex' : props.flex ? 'flex' : 'block')};
    flex-direction: ${(props) => getDirection(props)};
    align-items: ${(props) => getAlignment(props)};
    justify-content: ${(props) => getJustify(props)};
    flex-wrap: ${(props) => (props.flexwrap ? 'wrap' : null)};
    margin-bottom: ${(props) => getBottomIndent(props)};
    overflow: ${(props) => (props.overflow ? 'hidden' : null)};
    text-align: ${(props) => getTextAlign(props)};

    ${media.desktop1920} {
        margin-bottom: ${(props) => getDesktopBottomIndent(props)};
    }
`;

const ContainerBlock = styled.div.withConfig({ shouldForwardProp: shouldForwardRowProp })<StyledRowProps>`
    ${CommonCss}
`;
const ContainerSpan = styled.span.withConfig({ shouldForwardProp: shouldForwardRowProp })<StyledRowProps>`
    ${CommonCss}
`;
const ContainerListItem = styled.li.withConfig({ shouldForwardProp: shouldForwardRowProp })<StyledRowProps>`
    ${CommonCss}
`;

export interface RowProps extends StyledRowProps {
    type?: RowType;
    children?: React.ReactNode;
}

export const Row: React.FC<RowProps> = ({ type, children, ...rest }) => {
    const wrapperType = type ?? 'div';
    const commonProps: StyledRowProps = { ...rest, $isSpan: wrapperType === 'span' };

    if (wrapperType === 'listItem') {
        return <ContainerListItem {...commonProps}>{children}</ContainerListItem>;
    }
    if (wrapperType === 'span') {
        return <ContainerSpan {...commonProps}>{children}</ContainerSpan>;
    }
    return <ContainerBlock {...commonProps}>{children}</ContainerBlock>;
};
